/**
 * frontend/src/components/Layout/Sidebar.js
 * 
 * Vertical navigation sidebar component.
 * Provides quick access to the main dashboard and workspace settings (for admins).
 * Features tooltips and active state styling for navigation items.
 * 
 * Common components:
 * - NavItem: Helper component for individual navigation links with tooltips.
 * - Sidebar: Main sidebar container that determines which links to show based on user role.
 */
import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { FiHome, FiSettings, FiFileText, FiActivity, FiZap } from 'react-icons/fi';

const sidebarStyles = {
  sidebar: {
    position: 'fixed',
    left: 0,
    top: 0,
    bottom: 0,
    width: '72px',
    background: 'linear-gradient(180deg, #1e1b4b 0%, #312e81 100%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '65px 0',
    zIndex: 200,
  },
  nav: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
  navItem: {
    position: 'relative',
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'rgba(255, 255, 255, 0.6)',
    textDecoration: 'none',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
  },
  navItemActive: {
    background: 'rgba(99, 102, 241, 0.2)',
    color: '#a5b4fc',
  },
  navItemHover: {
    background: 'rgba(255, 255, 255, 0.1)',
    color: 'white',
  },
  tooltip: {
    position: 'absolute',
    left: '64px',
    background: '#1e293b',
    color: 'white',
    padding: '8px 12px',
    borderRadius: '8px',
    fontSize: '0.85rem',
    fontWeight: '500',
    whiteSpace: 'nowrap',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    zIndex: 1000,
  },
  tooltipArrow: {
    position: 'absolute',
    left: '-6px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: 0,
    height: 0,
    borderTop: '6px solid transparent',
    borderBottom: '6px solid transparent',
    borderRight: '6px solid #1e293b',
  },
};

const NavItem = ({ to, icon: Icon, label }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  return (
    <NavLink
      to={to}
      end={to === '/dashboard'}
      style={({ isActive }) => ({
        ...sidebarStyles.navItem,
        ...(isActive ? sidebarStyles.navItemActive : {}),
        ...(isHovered && !isActive ? sidebarStyles.navItemHover : {}),
      })}
      onMouseEnter={() => {
        setShowTooltip(true);
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        setShowTooltip(false);
        setIsHovered(false);
      }}
    >
      <Icon size={22} />
      {showTooltip && (
        <div style={sidebarStyles.tooltip}>
          <div style={sidebarStyles.tooltipArrow} />
          {label}
        </div>
      )}
    </NavLink>
  );
};

const Sidebar = ({ workspace }) => {
  const isAdmin = workspace?.user_role === 'admin';

  return (
    <aside style={sidebarStyles.sidebar}>
      <nav style={sidebarStyles.nav}>
        <NavItem
          to="/dashboard"
          icon={FiHome}
          label="Dashboard"
        />

        {isAdmin && (
          <NavItem
            to="/dashboard/templates"
            icon={FiFileText}
            label="Templates"
          />
        )}

        <NavItem
          to="/dashboard/audit-logs"
          icon={FiActivity}
          label="Audit Logs"
        />

        {isAdmin && (
          <NavItem
            to="/dashboard/settings"
            icon={FiSettings}
            label="Settings"
          />
        )}

        <NavItem
          to="/pricing"
          icon={FiZap}
          label="Upgrade"
        />
      </nav>
    </aside>
  );
};

export default Sidebar;
